import React from 'react';

/**
 * A queued mutation for one param key:
 * - `null` — remove the key from the URL entirely (the over-cap / clear bypass).
 * - `[]` — write the `?key=` empty marker (explicit "set to empty").
 * - `string[]` — set the key to exactly these encoded values.
 */
export type ParamMutation = string[] | null;

/** The value exposed by the search-param context. */
export interface SearchParamQueue {
  /** The params as react-router currently sees them (router-agnostic read). */
  readonly searchParams: URLSearchParams;
  /**
   * Queues a mutation for `key`. All mutations queued within a tick are applied
   * together on the next microtask in a single `setSearchParams` call.
   * @param key - the param key to mutate.
   * @param mutation - see {@link ParamMutation}.
   * @param replace - when true, replace the history entry instead of pushing.
   *   A single push request anywhere in the batch upgrades the whole flush to a
   *   push.
   */
  enqueue: (key: string, mutation: ParamMutation, replace: boolean) => void;
}

/** Context carrying the batched {@link SearchParamQueue}; `null` when unmounted. */
export const SearchParamContext = React.createContext<SearchParamQueue | null>(
  null,
);

/**
 * Reads the {@link SearchParamQueue} from context. Returns `null` when no
 * `SearchParamProvider` is mounted — callers degrade to memory-only mode in
 * that case.
 */
export function useSearchParamQueue(): SearchParamQueue | null {
  return React.useContext(SearchParamContext);
}
